import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function CompetencyEditor({ comp, stages, levels, onChange }) {
  const set = (patch) => onChange({ ...comp, ...patch });
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5" data-testid={`fw-competency-${comp.key}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label className="text-xs">Name</Label><Input value={comp.name} onChange={(e) => set({ name: e.target.value })} className="h-9 mt-1" data-testid={`fw-name-${comp.key}`} /></div>
        <div><Label className="text-xs">Catalogue search keywords (comma-separated, up to 8)</Label>
          <Input value={comp.keywordsText} onChange={(e) => set({ keywordsText: e.target.value })} className="h-9 mt-1" data-testid={`fw-keywords-${comp.key}`} placeholder="carbon accounting, GHG protocol" /></div>
      </div>
      <div className="mt-3"><Label className="text-xs">Description</Label><Textarea value={comp.description} onChange={(e) => set({ description: e.target.value })} rows={2} className="mt-1" data-testid={`fw-desc-${comp.key}`} /></div>
      <div className="mt-3">
        <Label className="text-xs">Required proficiency per stage</Label>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-1">
          {stages.map((s) => (
            <div key={s.key} className="bg-slate-50 rounded-lg p-2 border border-slate-100">
              <div className="text-[11px] text-slate-500 mb-1">{s.name}</div>
              <Select value={comp.required_levels?.[s.key] || "working"} onValueChange={(v) => set({ required_levels: { ...comp.required_levels, [s.key]: v } })}>
                <SelectTrigger className="h-8 text-xs bg-white" data-testid={`fw-level-${comp.key}-${s.key}`}><SelectValue /></SelectTrigger>
                <SelectContent>{levels.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function FrameworkEditor({ framework }) {
  const qc = useQueryClient();
  const [comps, setComps] = useState([]);
  useEffect(() => { setComps(framework.competencies.map((c) => ({ ...c, keywordsText: (c.keywords || []).join(", ") }))); }, [framework]);
  const save = useMutation({
    mutationFn: () => api.put("/academy/framework", {
      competencies: comps.map((c) => ({ key: c.key, name: c.name, description: c.description, required_levels: c.required_levels,
        keywords: c.keywordsText.split(",").map((k) => k.trim()).filter(Boolean) })),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["framework"] }); qc.invalidateQueries({ queryKey: ["journey"] }); toast.success("Framework saved — the next catalogue refresh uses the new keywords"); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  return (
    <div data-testid="framework-editor">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-xs text-slate-500 max-w-2xl">Six competency domains with the proficiency expected at each career stage. Keywords drive the LinkedIn Learning catalogue search; the count of stages is fixed.</p>
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending} data-testid="fw-save" className="bg-emerald-600 hover:bg-emerald-700">{save.isPending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save framework</Button>
      </div>
      <div className="space-y-4">
        {comps.map((c) => <CompetencyEditor key={c.key} comp={c} stages={framework.stages} levels={framework.levels} onChange={(n) => setComps((all) => all.map((x) => (x.key === n.key ? n : x)))} />)}
      </div>
    </div>
  );
}
